import Cookie from 'js-cookie';
import dayjs from 'dayjs';
import { CookieName, Platform } from '@/shared/enums';
import DeviceInfo from '@/shared/libs/device-info';
import { Emitter } from '@/shared/plugins/stats/emitter';
import EventType from './event-type';
import { getAdParams, getProductParams, resetDataLayer } from './helpers';

export default class GtmEmitter extends Emitter {
  constructor(stats, subscriber, liveManager) {
    super();
    this.stats = stats;
    this.subscriber = subscriber;
    this.liveManager = liveManager;
    this.subscriber.on('sync', this.updateUserParams, this);
    this.subscriber.on('sync:kids', this.updateUserParams, this);
    this.subscriber.on('sync:language', this.updateUserParams, this);
    this.init();
  }

  init() {
    this.defaultParams = {
      param_platform: Platform.BROWSER,
      param_device_os: DeviceInfo.getSystemName(),
      param_device_os_version: DeviceInfo.getSystemVersion(),
      param_service: 'aVOD',
      param_session_id: Cookie.get(CookieName.DEVICE_UID),
    };
    this.updateUserParams();
  }

  event(event, params = {}) {
    window.dataLayer.push({
      event,
      ...this.defaultParams,
      ...params,
    });
  }

  pageView({ pagePath }) {
    resetDataLayer();

    window.dataLayer.push({
      event: EventType.PAGE_VIEW,
      pagePath,
      pageTitle: document.title,
      ...this.defaultParams,
    });
  }

  updateUserParams() {
    if (this.subscriber.isLogged) {
      this.defaultParams = {
        ...this.defaultParams,
        param_customer_id: this.subscriber.profile.id,
        param_user_age: this.subscriber.dateOfBirth && dayjs().diff(this.subscriber.dateOfBirth, 'year'),
        param_user_gender: this.subscriber.gender?.toLowerCase(),
        param_user_language: this.subscriber.currentLanguage.toUpperCase(),
        param_user_mode: localStorage.isKidsMode === 'true' ? 'Kids' : 'Regular',
        param_user_status: 'Logged in',
      };
    } else {
      this.defaultParams = {
        ...this.defaultParams,
        param_user_language: this.subscriber.currentLanguage.toUpperCase(),
        param_user_mode: localStorage.isKidsMode === 'true' ? 'Kids' : 'Regular',
        param_user_status: 'Anonymous',
      };
      delete this.defaultParams.param_customer_id;
    }
  }

  login({ isAutoLogin }) {
    this.event(EventType.LOGIN, { param_autologin: isAutoLogin });
  }

  logout({ profile }) {
    this.event(EventType.LOGOUT, {
      param_customer_id: profile.id,
    });
  }

  search({ keyword }) {
    this.event(EventType.SEARCH, {
      param_keyword: keyword,
    });
  }

  showSignUpForm() {
    this.event(EventType.SHOW_SIGN_UP_FORM);
  }

  errorWhilePlaying({ product, playerCurrentTime, errorCode }) {
    this.event(EventType.ERROR_WHILE_PLAYING, {
      ...getProductParams(product),
      param_time_of_error: playerCurrentTime,
      param_error_code: errorCode,
    });
  }

  startPlaying({ product }) {
    this.event(EventType.START_PLAYING, getProductParams(product));
  }

  pausePlaying({ product, playerCurrentTime }) {
    this.event(EventType.PAUSE_PLAYING, {
      ...getProductParams(product),
      param_time_of_pause: playerCurrentTime,
    });
  }

  stopPlaying({ product, playerCurrentTime }) {
    const params = {
      ...getProductParams(product),
      param_time_of_stop: playerCurrentTime,
    };
    this.event(EventType.STOP_PLAYING, params);
  }

  videoProgress({ product, params }) {
    this.event(EventType.VIDEO_PROGRESS, {
      ...getProductParams(product),
      param_progress: params.progress,
    });
  }

  adClick({ adParams, product }) {
    this.event(EventType.AD_CLICKED, {
      ...getAdParams(adParams),
      ...getProductParams(product),
    });
  }

  adFinish({ adParams, product }) {
    this.event(EventType.AD_FINISH, {
      ...getAdParams(adParams),
      ...getProductParams(product),
    });
  }

  adStart({ adParams, product }) {
    this.event(EventType.AD_START, {
      ...getAdParams(adParams),
      ...getProductParams(product),
    });
  }
}
